import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/ConfiguracoesSistema_SaveInput.ts';
import '../contracts/ConfiguracoesSistemaFranquia_DeleteInput.ts';

import '../contracts/ConfiguracoesSistemaFranquia_DeleteResponse';
import '../contracts/ConfiguracoesSistemaFranquia_ListWithFranquiaDataResponse';
import '../contracts/ConfiguracoesSistemaFranquia_SaveResponse';
import type { ConfiguracoesSistemaFranquia_SaveInput } from '../contracts/ConfiguracoesSistemaFranquia_SaveInput';
/**
 * Composable for Configurações Sistema Franquia (System Configuration per Franchise) operations
 * Provides methods to interact with the System Configuration API
 *
 * System configurations have default values defined system-wide,
 * and franchises can override them with franchise-specific values.
 */
export function useConfiguracoesSistemaFranquia() {
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Fetch all configurations with franchise-specific values
   * Returns merged data: system-wide configs with franchise overrides
   * @param {number} franquiaId - Franchise ID
   * @returns {Promise<Array>} Configurations list with fields: nome, descricao, valor_padrao, valor
   */
  const fetchConfigsWithFranquiaData = async (franquiaId: number) => {
    log('useConfiguracoesSistemaFranquia', 'fetchConfigsWithFranquiaData', 'Fetching configs for franchise', franquiaId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/config/configuracoes_sistema_franquia/list_with_franquia_data/${franquiaId}`, { responseContract: 'ConfiguracoesSistemaFranquia_ListWithFranquiaDataResponse' });
      log('useConfiguracoesSistemaFranquia', 'fetchConfigsWithFranquiaData', 'Response', response);

      if (response.success && response.dados) {
        return response.dados;
      } else {
        throw new Error('Erro ao buscar configurações');
      }
    } catch (err) {
      log('useConfiguracoesSistemaFranquia', 'fetchConfigsWithFranquiaData', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Save configuration (auto-detects create vs update)
   * Backend checks if config exists for franchise and calls appropriate method
   * @param {Object} configData - Configuration data
   * @param {string} configData.nome - Config key name
   * @param {number} configData.franquia_id - Franchise ID
   * @param {string} configData.valor - Config value
   * @returns {Promise<Object>} Saved configuration
   */
  const saveConfig = async (configData: ConfiguracoesSistemaFranquia_SaveInput) => {
    log('useConfiguracoesSistemaFranquia', 'saveConfig', 'Saving config', configData);
    isLoading.value = true;
    error.value = null;

    try {
      validate('ConfiguracoesSistema_SaveInput', configData);
      const response = await httpService.post('/index.php/config/configuracoes_sistema_franquia/save', configData, { responseContract: 'ConfiguracoesSistemaFranquia_SaveResponse' });
      log('useConfiguracoesSistemaFranquia', 'saveConfig', 'Response', response);

      if (response.success && response.dados) {
        return response.dados;
      } else {
        throw new Error('Erro ao salvar configuração');
      }
    } catch (err) {
      log('useConfiguracoesSistemaFranquia', 'saveConfig', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete franchise-specific configuration
   * @param {string} nome - Config key name
   * @returns {Promise<boolean>} Success status
   */
  const deleteConfig = async (nome: string) => {
    log('useConfiguracoesSistemaFranquia', 'deleteConfig', 'Deleting config', nome);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/config/configuracoes_sistema_franquia/delete/${nome}`, null, { inputContract: 'ConfiguracoesSistemaFranquia_DeleteInput', responseContract: 'ConfiguracoesSistemaFranquia_DeleteResponse' });
      log('useConfiguracoesSistemaFranquia', 'deleteConfig', 'Response', response);

      if (response.success) {
        return true;
      } else {
        throw new Error('Erro ao deletar configuração');
      }
    } catch (err) {
      log('useConfiguracoesSistemaFranquia', 'deleteConfig', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    isLoading,
    error,

    // Methods
    fetchConfigsWithFranquiaData,
    saveConfig,
    deleteConfig
  };
}
